import React, { useState, useEffect } from 'react';
import { Card, Space, Typography, Spin, Alert, Row, Col, Tooltip } from 'antd';
import { 
  ThunderboltOutlined, 
  EyeOutlined, 
  CompassOutlined, 
  DashboardOutlined,
  WarningOutlined 
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * 🌦️ WeatherPopup - Displays detailed weather information for a location
 * Shows current conditions, hourly forecast, and weather alerts
 * Used when clicking/hovering on site markers
 */
const WeatherPopup = ({ 
  weatherManager, 
  latitude, 
  longitude, 
  siteName = 'Location',
  visible = false,
  onClose = () => {} 
}) => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load weather data when popup becomes visible
  useEffect(() => {
    if (visible && weatherManager && latitude && longitude) {
      loadWeatherData();
    }
  }, [visible, weatherManager, latitude, longitude]);

  const loadWeatherData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await weatherManager.getLocationWeather(latitude, longitude);
      setWeatherData(data);
    } catch (err) {
      console.error('🌦️ Error loading weather data:', err);
      setError('Unable to load weather data');
    } finally {
      setLoading(false);
    }
  };

  // Get weather icon URL
  const getWeatherIconUrl = (iconCode) => {
    return `https://openweathermap.org/img/w/${iconCode}.png`;
  };

  // Format temperature with color coding
  const getTempColor = (temp) => {
    if (temp < 0) return '#0066cc';
    if (temp < 10) return '#00aaff';
    if (temp < 25) return '#ff9900';
    return '#ff3300';
  };

  if (!visible) return null;

  return (
    <div 
      style={{
        position: 'absolute',
        top: '50px',
        right: '20px',
        zIndex: 1001,
        maxWidth: '350px',
        pointerEvents: 'auto'
      }}
    >
      <Card
        size="small"
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}
        bodyStyle={{ padding: '16px' }}
        extra={
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#999',
              padding: '0',
              lineHeight: 1
            }}
          >
            ×
          </button>
        }
      >
        {/* Header */}
        <div style={{ marginBottom: '12px' }}>
          <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
            🌦️ Weather at {siteName}
          </Title>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {latitude.toFixed(4)}, {longitude.toFixed(4)}
          </Text>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '8px' }}>
              <Text type="secondary">Loading weather data...</Text>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <Alert
            message="Weather Data Unavailable"
            description={error}
            type="warning"
            showIcon
            style={{ marginBottom: '12px' }}
          />
        )}

        {/* Weather data */}
        {weatherData && !loading && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Current conditions */}
            <Card 
              size="small" 
              style={{ backgroundColor: '#f8f9fa' }}
              bodyStyle={{ padding: '12px' }}
            >
              <Row align="middle" gutter={[12, 8]}>
                <Col span={6}>
                  <img 
                    src={getWeatherIconUrl(weatherData.current.icon)}
                    alt={weatherData.current.description}
                    style={{ width: '48px', height: '48px' }}
                  />
                </Col>
                <Col span={18}>
                  <div>
                    <Text 
                      strong 
                      style={{ 
                        fontSize: '24px', 
                        color: getTempColor(weatherData.current.temperature) 
                      }}
                    >
                      {weatherData.current.temperature}°C
                    </Text>
                  </div>
                  <div>
                    <Text style={{ fontSize: '13px', textTransform: 'capitalize' }}>
                      {weatherData.current.description}
                    </Text>
                  </div>
                </Col>
              </Row>

              {/* Additional current conditions */}
              <Row gutter={[8, 4]} style={{ marginTop: '8px' }}>
                <Col span={12}>
                  <Space size={4}>
                    <CompassOutlined style={{ color: '#1890ff' }} />
                    <Text style={{ fontSize: '11px' }}>
                      {weatherData.current.windSpeed} km/h
                    </Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space size={4}>
                    <EyeOutlined style={{ color: '#52c41a' }} />
                    <Text style={{ fontSize: '11px' }}>
                      {weatherData.current.humidity}%
                    </Text>
                  </Space>
                </Col>
                <Col span={12}>
                  <Space size={4}>
                    <DashboardOutlined style={{ color: '#722ed1' }} />
                    <Text style={{ fontSize: '11px' }}>
                      {weatherData.current.pressure} hPa
                    </Text>
                  </Space>
                </Col>
              </Row>
            </Card>

            {/* Hourly forecast */}
            <div>
              <Text strong style={{ fontSize: '13px', color: '#666' }}>
                Next 6 Hours
              </Text>
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                overflowX: 'auto', 
                paddingTop: '8px',
                paddingBottom: '4px'
              }}>
                {weatherData.hourly.map((hour, index) => (
                  <div 
                    key={index}
                    style={{
                      minWidth: '50px',
                      textAlign: 'center',
                      padding: '6px 4px',
                      backgroundColor: 'rgba(24, 144, 255, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(24, 144, 255, 0.1)'
                    }}
                  >
                    <Text style={{ fontSize: '10px', color: '#666' }}>
                      {hour.time}
                    </Text>
                    <div style={{ margin: '2px 0' }}>
                      <img 
                        src={getWeatherIconUrl(hour.icon)}
                        alt={hour.description}
                        style={{ width: '24px', height: '24px' }}
                      />
                    </div>
                    <Text 
                      strong 
                      style={{ 
                        fontSize: '11px',
                        color: getTempColor(hour.temperature)
                      }}
                    >
                      {hour.temperature}°
                    </Text>
                    {hour.precipitation > 0 && (
                      <div>
                        <Text style={{ fontSize: '9px', color: '#1890ff' }}>
                          {Math.round(hour.precipitation)}%
                        </Text>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Weather alerts */}
            {weatherData.alerts && weatherData.alerts.length > 0 && (
              <div>
                <Text strong style={{ fontSize: '13px', color: '#ff4d4f' }}>
                  <WarningOutlined /> Weather Alerts
                </Text>
                <div style={{ marginTop: '6px' }}>
                  {weatherData.alerts.slice(0, 2).map((alert, index) => (
                    <Alert
                      key={index}
                      message={alert.event}
                      description={alert.description?.substring(0, 100) + '...'}
                      type="warning"
                      size="small"
                      showIcon
                      style={{ marginBottom: '6px' }}
                    />
                  ))}
                </div>
              </div>
            )}
          </Space>
        )}

        {/* Footer */}
        <div style={{ 
          marginTop: '12px', 
          paddingTop: '8px', 
          borderTop: '1px solid #f0f0f0',
          textAlign: 'center'
        }}>
          <Text style={{ fontSize: '10px', color: '#999' }}>
            Real-time data from OpenWeatherMap
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default WeatherPopup; 